import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Box, Typography, CircularProgress, Alert, Paper, Grid, Divider, List, ListItem,
    ListItemText, Card, CardContent, Chip, Container, Accordion, AccordionSummary,
    AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
    ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, Label, ReferenceArea
} from 'recharts';
import api from './api';
import config from './config';

// Helper to get current user's ID from localStorage
const getCurrentUserId = () => {
    return localStorage.getItem('userId');
};

const getAcsStyleName = (proactivity, reactivity) => {
    if (proactivity === null || reactivity === null) return "Not Determined";
    const pro = Number(proactivity);
    const react = Number(reactivity);
    if (pro > 0 && react > 0) return "Expressive";
    if (pro < 0 && react < 0) return "Analytical";
    if (pro > 0 && react < 0) return "Driver";
    if (pro < 0 && react > 0) return "Amiable";
    if (pro === 0 && react > 0) return "Amiable Tendency";
    if (pro === 0 && react < 0) return "Analytical Tendency";
    if (react === 0 && pro > 0) return "Driver Tendency";
    if (react === 0 && pro < 0) return "Analytical Tendency";
    if (pro === 0 && react === 0) return "Balanced/Neutral";
    return "Not Determined";
};

const acsStyleDefinitions = {
    "Analytical": "Analyticals are process oriented, structured, and prefer to use strategies that have proven successful. They are motivated by data and detail, intellectual rigor, and accuracy. Analyticals like to be right – usually because they are due to their diligent study of alternatives. This can sometimes lead Analyticals to fall into analysis paralysis, a state in which they are so consumed by considering their options that they fail to make a decision. When working with Analyticals, their teammates should try to match their rigor and accuracy, mitigate risks, and think before answering the Analytical’s (many) thoughtful questions.",
    "Amiable": "Amiables are people oriented, generous with their time, and bridge-builders on teams. They are motivated by harmony, building consensus, and social interactions. Amiables are often the glue that holds teams together and often most attuned to the feelings of their teammates. Amiables can sometimes get off track during team meetings because they are so focused on nurturing the team’s relationships. When working with Amiables, their teammates should provide appropriate opportunities to socialize, be empathic, provide a predictable environment, and explain the context of tasks and decisions.",
    "Expressive": "Expressives are innovation oriented, forward thinking, creative, unstructured, and open to coaching. They are motivated by aesthetics, recognition, and status. Expressives don’t mind being in the spotlight, especially if it gives them a chance to express their unique perspectives. This can sometimes lead Expressives to dominate conversations or pursue unrealistic or complex ideas that are unnecessary for the tasks at hand. When working with Expressives, their teammates should give them opportunities to express themselves, give them credit when they do a good job, don’t embarrass them in front of others, and keep their explanations brief.",
    "Driver": "Drivers are task oriented, decisive, direct, and structured. They like to win and will develop strategies to ensure they do. Drivers prefer to be in control and move at a fast pace. They are bottom-line organizers who start with an end goal in mind and create to-do lists to systematically achieve that goal. Because they are so structured in their approach, Drivers may dominate teammates who are less assertive and dismiss those who they deem to have less credibility due to different work styles. When working with Drivers, their teammates should save them time, provide them with options (from which they will make quick selections), and be direct and concise."
};

const rephraseAcsDefinition = (styleName, definition) => {
    if (!definition) return "Information for this style is not available.";
    let rephrased = definition;
    const userFinalSentences = {
        "Analytical": "When working with you, your teammates should try to match your rigor and accuracy, mitigate risks, and think before answering your (many) thoughtful questions.",
        "Amiable": "When working with you, your teammates should provide appropriate opportunities to socialize, be empathic, provide a predictable environment, and explain the context of tasks and decisions.",
        "Expressive": "When working with you, your teammates should give you opportunities to express yourself, give you credit when they do a good job, not embarrass you in front of others, and keep their explanations brief.",
        "Driver": "When working with you, your teammates should save you time, provide you with options (from which you will make quick selections), and be direct and concise."
    };
    const originalFinalSentenceRegex = /When working with (Analyticals|Amiables|Expressives|Drivers), their teammates should.+$/;
    if (userFinalSentences[styleName]) {
        rephrased = rephrased.replace(originalFinalSentenceRegex, userFinalSentences[styleName]);
    }
    const firstSentenceRegex = new RegExp(`^(${styleName}s) are`, 'i');
    if (firstSentenceRegex.test(rephrased)) {
        rephrased = `As a/an ${styleName}, you are ${rephrased.substring((styleName + "s are").length)}`;
    } else { 
        const firstSentenceSimpleRegex = new RegExp(`^(${styleName}) are`, 'i');
         if (firstSentenceSimpleRegex.test(rephrased)) {
            rephrased = rephrased.replace(firstSentenceSimpleRegex, `As a/an ${styleName}, you are`);
        }
    }
    const styleNamePluralGlobalRegex = new RegExp(`\\b${styleName}s\\b`, 'gi');
    const styleNameSingularGlobalRegex = new RegExp(`\\b${styleName}\\b`, 'gi');
    let sentences = rephrased.split(/(?<=[.!?])\s+/); 
    for (let i = 1; i < sentences.length; i++) { 
        if (sentences[i]) { 
            sentences[i] = sentences[i].replace(styleNamePluralGlobalRegex, "you");
            sentences[i] = sentences[i].replace(styleNameSingularGlobalRegex, "you");
        }
    }
    rephrased = sentences.join(" ");
    rephrased = rephrased.replace(/\bThey are\b/g, "You are").replace(/\bthey are\b/g, "you are")
                         .replace(/\bThey like\b/g, "You like").replace(/\bthey like\b/g, "you like")
                         .replace(/\bThey don’t\b/g, "You don’t").replace(/\bthey don’t\b/g, "you don’t")
                         .replace(/\bThey prefer\b/g, "You prefer").replace(/\bthey prefer\b/g, "you prefer")
                         .replace(/\bThey may\b/g, "You may").replace(/\bthey may\b/g, "you may")
                         .replace(/\bThey can\b/g, "You can").replace(/\bthey can\b/g, "you can")
                         .replace(/\bThey don’t mind\b/g, "You don’t mind").replace(/\bthey don’t mind\b/g, "you don’t mind")
                         .replace(/\bThey will develop\b/g, "You will develop").replace(/\bthey will develop\b/g, "you will develop")
                         .replace(/\btheir\b/g, "your").replace(/\bthem\b/g, "you")
                         .replace(/\bthe Analytical’s\b/g, "your"); 
    return rephrased;
};

const wepDimensionExplanations = {
    "Context": {
        dimension: "Context",
        measure: "This dimension measures your relative preference for direct versus subtle communication.",
        low: "A lower number means that you prefer to communicate using low-context, precise, simple, explicit, and clear messages.",
        high: "A higher number means that you prefer to communicate using high-context, sophisticated, nuanced, and layered messages."
    },
    "Feedback": {
        dimension: "Feedback",
        measure: "This dimension measures your relative preference for directness versus sensitivity when giving and receiving feedback.",
        low: "A lower number means that you prefer to give and receive negative feedback without considering emotion.",
        high: "A higher number means that you are more likely to consider the recipient's feelings when delivering negative feedback and expect others to do the same for you."
    },
    "Debate": {
        dimension: "Debate",
        measure: "This dimension measures your relative preference for prioritizing theory versus evidence in persuasion.",
        low: "A lower number means that you prefer that a theoretical basis is established first.",
        high: "A higher number means that you prefer the use of tangible examples first."
    },
    "Hierarchy": {
        dimension: "Hierarchy",
        measure: "This dimension measures relative preference for flat versus layered organizational structures.",
        low: "A lower number means that you prefer leadership structures in which the lines between leaders and followers are blurred.",
        high: "A higher number means you prefer stratified structures in which there are clear leaders and followers or subordinates."
    },
    "Decision-Making": {
        dimension: "Decision-Making",
        measure: "This dimension measures your relative preference for making decisions as a group versus top-down by a leader or leaders.",
        low: "A lower number means you prefer consensus-based decision making.",
        high: "A higher number means you prefer allowing leaders to make decisions for the group."
    },
    "Relationships": {
        dimension: "Relationships",
        measure: "This dimension measures your relative preference for building trust through work time versus social time.",
        low: "A lower number means that you prefer to build trust through completing work-related tasks with others.",
        high: "A higher number means that you prefer to build trust by spending non-work time together."
    },
    "Conflict": {
        dimension: "Conflict",
        measure: "This dimension measures your relative preference for addressing disagreements publicly versus privately.",
        low: "A lower number means that you are likely to be comfortable with confrontation and airing grievances in public.",
        high: "A higher number means that you are more likely to avoid confrontation or prefer to keep disagreements private."
    },
    "Chronemics": {
        dimension: "Chronemics",
        measure: "This dimension measures your relative preference for structured versus flexible schedules and deadlines.",
        low: "A lower number means that you prefer structured timelines and deadlines.",
        high: "A higher number means that you prefer flexible timelines and deadlines and treating schedules as a suggestion."
    }
};

const pmvInterestAreaDefinitions = {
    "Status Interest": "You are likely to thrive in cultures in which you have authority, are recognized for your hard work, and have the opportunity to both work hard and play hard.",
    "Social Interest": "You are likely to thrive in cultures in which people come first, are supported, and follow well-established behavioral norms.",
    "Financial Interest": "You are likely to thrive in cultures in which you are highly compensated, where recognition is monetary, and where you are guaranteed financial stability.",
    "Decision-Making Interest": "You are likely to thrive in cultures that are driven by data and the quality of work produced."
};

const teamCultureStatements = {
    Recognition: {
        low: "This team may not prioritize public acknowledgement or individual praise as primary motivators. Focus might be more on collective achievement or intrinsic rewards.",
        high: "This team is likely driven by opportunities for visibility and public recognition. They value being acknowledged for their contributions and achievements."
    },
    Power: {
        low: "Influence and control may not be strong drivers for this team. They might prefer collaborative environments with distributed leadership.",
        high: "This team values achievement, influence, and competition. They are likely motivated by challenges and opportunities to demonstrate their capabilities and lead."
    },
    Hedonism: {
        low: "Fun and excitement might be secondary to other work aspects for this team. They may prefer more focused and predictable work environments.",
        high: "This team enjoys a lively and varied work atmosphere. They are motivated by pleasure, excitement, and new experiences."
    },
    Altruism: {
        low: "While not uncaring, direct altruistic activities or societal impact might not be the primary driver for this team's day-to-day motivation. They may focus on other goals.",
        high: "This team is strongly motivated by helping others and making a positive impact. They value opportunities to improve society or the workplace."
    },
    Affiliation: {
        low: "This team may be more independent or task-focused, with less emphasis on constant social interaction or group belonging as a core motivator.",
        high: "Frequent social interaction and a strong sense of belonging are important for this team. Networking and collaborative group work are likely valued."
    },
    Tradition: {
        low: "This team may be more open to change and less bound by historical precedents or established norms. They might favor innovation over convention.",
        high: "This team values established customs, history, and traditional virtues. They are likely to respect and uphold organizational traditions."
    },
    Commerce: { 
        low: "Financial gain or business-centric activities might not be the strongest motivators for this team. They may prioritize other aspects like impact or work-life balance.",
        high: "This team is motivated by business activities, financial success, and investment opportunities. They are likely attuned to profits and market dynamics."
    },
    Security: { 
        low: "This team may be comfortable with ambiguity and change, with less emphasis on predictability and stability as core needs.",
        high: "Stability, certainty, and a predictable environment are key motivators for this team. They value structure, order, and planning."
    },
    Aesthetics: {
        low: "Artistic expression or the 'look and feel' of things might not be primary drivers for this team. Functionality may take precedence over form.",
        high: "This team is motivated by creativity, artistic self-expression, and the quality of their surroundings. They appreciate attention to design and aesthetics."
    },
    Science: {
        low: "An analytical, data-driven approach might be less of a core motivator for this team. They may rely more on intuition or other problem-solving methods.",
        high: "This team is driven by ideas, technology, and rational problem-solving. They value analytical approaches and understanding how things work."
    }
};

const informalRoleFeedbackStatements = {
    "Results": {
        high: "Your team is heavily results-oriented, so make sure that you don’t lose sight of relationships, both inside and outside your group. A strong results focus might lead your team to focus mostly on short-term wins at the expense of perspective and long-term direction.",
        medium: "Your team should have the capacity to focus on results as they align with other team objectives. You should have clarity around goals and priorities, roles and responsibilities, and performance standards. Your team is likely to be able to balance short-term focus with long-term vision.",
        none: "Your team does not have any members who selected Results as their preferred informal team role. This means that you all must make a conscious effort to maintain accountability of all members. Without a dedicated Results-focused team member, your group may drift from your goals over time. As a team, occasionally take the time to reconfirm your goals and invite outsiders to play devil’s advocate and challenge your team to refine your goals."
    },
    "Relationships": {
        high: "Your team is heavily relationship-oriented, which can lead to your team failing to hold each other accountable out of a sincere desire to maintain your relationships. Make sure that you give each other clear feedback, make measurable commitments to each other, and follow through on those commitments.",
        medium: "Your team should have the capacity to maintain close and productive relationships among all members. You are likely to be supportive and cohesive, with a unified spirit while still providing feedback to each other in a constructive way.",
        none: "Your team does not have any members who selected Relationships as their preferred informal team role. While this could mean that your team will be direct in giving each other feedback without hesitation, this could also lead to disruptive conflict later in your team’s life cycle. Ensure that you make time to celebrate success together and spend time socializing outside of work. Also ensure that you are meeting the needs and expectations of external stakeholders."
    },
    "Process": {
        high: "Your team is heavily process-oriented. While your team is likely strong when it comes to details, it could also mean that you may lack the agility to adapt when necessary. As a team, remember that sometimes you can achieve better results by moving faster and compromising a little on process. Occasionally ask yourselves what you would let go of if your time or resources were cut in half.",
        medium: "Your team should have the capacity to ensure effective processes and follow through. You are likely to be productive, organized, and good at following plans while finishing projects on time.",
        none: "Your team does not have any members who selected the Process preferred informal team role. This may indicate a critical lack of self-discipline or good process. It is imperative that your team takes the time to develop clear protocols on project plans. Make commitments to each other to hold each other accountable for completing tasks on time. You should consider adding a step toward the end of every project in which at least one member of the team is responsible for double-checking the details and quality of the final product."
    },
    "Innovation": {
        high: "Your team is heavily innovation-oriented, which indicates you are creative and visionary. This can also mean that you may spend too much time focusing on the goal and not enough on the actual implementation. Make sure that you are ground your team’s thinking and clearly define – and execute – a plan of action to achieve your big goals.",
        medium: "Your team should have the capacity to ensure a clear vision and long-term strategy. You are likely to adapt to changing conditions and capable of dealing with unexpected problems as they arise.",
        none: "Your team does not have any members who selected the Innovation informal team role. This might lead to a lack of unifying purpose or vision for your work. Your team should take the time to discuss how you want your team to be perceived by outsiders and the values you collectively want to convey to them. As a team, define what unites and unifies you; what are your ultimate team goals?"
    },
    "Pragmatist": {
        high: "Your team is heavily pragmatism-oriented, which may inhibit you from fully developing worthwhile ideas. When a team too easily dismisses ideas because they at first seem impractical, it limits its options down the line. Practice saying “yes, and” to each other instead of “no, but”.",
        medium: "Your team should have the capacity to follow a sensible and practical approach to achieving its goals. You are likely to have a healthy dose of devil’s advocacy without inhibiting productive creativity.",
        none: "Your team does not have any members who selected the Pragmatist informal team role. This could lead to your team pursuing unrealistic strategies that you won’t have the capacity to effectively implement. It is crucial for your team to establish systems that will check your strategies for practicality. Consider appointing at least one member to serve as a devil’s advocate during team meetings and work sessions to purposefully provide realistic checks on the team’s work."
    }
};


const CustomACSCircle = (props) => {
    const { cx, cy, fill, name, type, payload } = props; 
    let radius = 8;
    let strokeColor = "#666";
    let strokeWidth = 1;
    let shape;

    if (cx == null || cy == null) return null;

    if (type === 'team_average') {
        radius = 12;
        strokeColor = "#000";
        strokeWidth = 2;
        const points = [];
        for (let i = 0; i < 5; i++) {
            points.push(`${cx + radius * Math.cos(Math.PI / 2 + (2 * Math.PI * i) / 5)},${cy - radius * Math.sin(Math.PI / 2 + (2 * Math.PI * i) / 5)}`);
            points.push(`${cx + (radius / 2) * Math.cos(Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / 5)},${cy - (radius / 2) * Math.sin(Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / 5)}`);
        }
        shape = <polygon points={points.join(' ')} fill={fill} stroke={strokeColor} strokeWidth={strokeWidth} />;
    } else if (type === 'current_user') {
        radius = 10;
        strokeColor = fill; 
        strokeWidth = 2.5;
        shape = <circle cx={cx} cy={cy} r={radius} fill={fill} stroke={strokeColor} strokeDasharray="3 3" strokeWidth={strokeWidth}/>;
    }
    else { 
        shape = <circle cx={cx} cy={cy} r={radius} fill={fill} stroke={strokeColor} strokeWidth={strokeWidth}/>;
    }
    return shape;
};

const CustomTeamWEPShape = (props) => {
    const { cx, cy, fill, type, payload } = props; 
    if (cx == null || cy == null) return null;
    let radius = 8;
    let stroke = "#555";
    let strokeWidth = 1;
    let actualFill = fill;

    if (payload && payload.isCurrentUserPoint) { 
        actualFill = '#ff9800'; 
        stroke = '#e65100';    
        strokeWidth = 2;
        radius = 9; 
    }

    if (type === 'team_average_wep') {
        return (
            <rect
                x={cx - radius}
                y={cy - radius}
                width={radius * 2}
                height={radius * 2}
                fill={actualFill} 
                stroke="#000"
                strokeWidth={1.5}
                transform={`rotate(45 ${cx} ${cy})`}
            />
        );
    }
    return <circle cx={cx} cy={cy} r={radius * (payload && payload.isCurrentUserPoint ? 1 : 0.8)} fill={actualFill} stroke={stroke} strokeWidth={strokeWidth} />;
};


function SelfPerceptionInventoryResults({ cohortId: cohortIdFromProps, teamId: teamIdFromProps, currentUserId: userIdFromProps }) {
    const { userId: userIdFromParams, cohortId: cohortIdFromParams } = useParams();
    const location = useLocation();
    
    const [individualResults, setIndividualResults] = useState(null);
    const [teamSpiReport, setTeamSpiReport] = useState(null);
    const [loadingIndividual, setLoadingIndividual] = useState(true);
    const [loadingTeam, setLoadingTeam] = useState(false); 
    const [error, setError] = useState(null);
    const [teamError, setTeamError] = useState(null);

    const userId = useMemo(() => 
        userIdFromProps || 
        (userIdFromParams ? userIdFromParams : null) || 
        getCurrentUserId(), 
    [userIdFromProps, userIdFromParams]);

    const cohortId = useMemo(() => 
        cohortIdFromProps || 
        (cohortIdFromParams ? parseInt(cohortIdFromParams, 10) : null) ||
        (location.state?.cohortId ? parseInt(location.state.cohortId, 10) : null) ||
        (localStorage.getItem('userCohortId') ? parseInt(localStorage.getItem('userCohortId'), 10) : null),
    [cohortIdFromProps, cohortIdFromParams, location.state]);

    const teamId = useMemo(() => 
        teamIdFromProps || 
        (location.state?.teamId ? parseInt(location.state.teamId, 10) : null) ||
        (localStorage.getItem('userTeamId') ? parseInt(localStorage.getItem('userTeamId'), 10) : null),
    [teamIdFromProps, location.state]);


    useEffect(() => {
        if (!userId || cohortId === null || cohortId === undefined) {
            setError("User ID or Cohort ID is missing. Cannot fetch individual SPI results.");
            setLoadingIndividual(false);
            return;
        }
        const fetchIndividualResults = async () => {
            setLoadingIndividual(true); setError(null);
            try {
                const response = await api.get(`${config.backendUrl}/api/spi/results/${userId}/${cohortId}`);
                setIndividualResults(response.data);
            } catch (err) {
                setError(err.response?.data?.message || err.message || "Failed to load your SPI results.");
                console.error("Error fetching individual SPI results:", err);
            } finally {
                setLoadingIndividual(false);
            }
        };
        fetchIndividualResults();
    }, [userId, cohortId]);

    useEffect(() => {
        if (teamId && cohortId) { 
            const fetchTeamReport = async () => {
                setLoadingTeam(true); setTeamError(null);
                try {
                    const response = await api.get(`${config.backendUrl}/api/team/${teamId}/spi_report/${cohortId}`);
                    setTeamSpiReport(response.data); 
                } catch (err) {
                    console.error("Error fetching team SPI report:", err);
                    setTeamError(err.response?.data?.message || "Failed to load team SPI report.");
                    setTeamSpiReport(null); 
                } finally {
                    setLoadingTeam(false);
                }
            };
            fetchTeamReport();
        } else {
            setLoadingTeam(false); 
            setTeamSpiReport(null); 
            if (!teamId) console.log("Team ID not available for team SPI report.");
        }
    }, [teamId, cohortId]);


    const renderAttitudinalCommunicationStyles = (data) => {
        if (!data || !data.summary_calculations?.attitudinal_communication_style) return null;
        const acsRaw = data.summary_calculations.attitudinal_communication_style;
        const compositeProactivity = (acsRaw.proactivity_score || 0) / 2;
        const compositeReactivity = (acsRaw.reactivity_score || 0) / 2;
        const userAcsStyleName = getAcsStyleName(compositeProactivity, compositeReactivity);
        let userStyleExplanation = "Explanation for your style will appear here.";
        if (acsStyleDefinitions[userAcsStyleName]) {
            userStyleExplanation = rephraseAcsDefinition(userAcsStyleName, acsStyleDefinitions[userAcsStyleName]);
        } else if (userAcsStyleName === "Balanced/Neutral" || userAcsStyleName.includes("Tendency")) {
            userStyleExplanation = `Your results indicate a style of "${userAcsStyleName}", representing a specific blend or tendency in communication preferences. More detailed insights for this profile can be developed.`;
        }
        const acsIntroText = "Your approach to communicating with others is largely defined by your attitudes toward work as defined by two traits, Proactivity and Reactivity. Proactivity is a measure of how likely you are to prioritize asserting your thoughts, opinions, or needs in the work environment. Reactivity is a measure of how likely you are to prioritize emoting and interacting with others. Your Attitudinal Communication Style reflects your innate or natural communication tendencies and may differ from your Behavioral Communication Style.";
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>Attitudinal Communication Styles</Typography>
                    <Typography variant="body1" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{acsIntroText}</Typography>
                    <Typography variant="body1" sx={{textAlign: 'left'}}>Your Composite Proactivity Score: {compositeProactivity.toFixed(1)}</Typography>
                    <Typography variant="body1" sx={{mb: 1, textAlign: 'left'}}>Your Composite Reactivity Score: {compositeReactivity.toFixed(1)}</Typography>
                    <Typography variant="h6" sx={{mt: 2, mb: 1, textAlign: 'left'}}>Your Identified Style: <Chip label={userAcsStyleName} color="primary" variant="filled" /></Typography>
                    <Box id="acs-explanation-user" sx={{mt:1, mb: 3}}>
                        <Typography variant="body1" sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>{userStyleExplanation}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{mt:3, mb:1, textAlign: 'left'}}>Understanding Other Styles</Typography>
                    {Object.entries(acsStyleDefinitions)
                        .filter(([styleName]) => {
                            if (userAcsStyleName === styleName) return false;
                            if (userAcsStyleName.includes(styleName) && userAcsStyleName.length > styleName.length && userAcsStyleName.includes("Tendency")) return false; 
                            return true;
                        })
                        .map(([styleName, definition]) => (
                            <Box key={styleName} sx={{mt:1, mb: 2}}>
                                <Typography variant="subtitle1" sx={{fontWeight:'bold', textAlign: 'left'}}>{styleName}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>{definition}</Typography>
                            </Box>
                        ))}
                </CardContent>
            </Card>
        );
    };

    const renderACSGraph = (data, title = "Attitudinal Communication Style Graph", graphDataOverride = null) => {
        if (!data && !graphDataOverride) {
            return <Box sx={{textAlign: 'center', my:2}}><Typography color="textSecondary">ACS Graph data not available.</Typography></Box>;
        }
        
        let plotData;
        if (graphDataOverride) {
            plotData = graphDataOverride;
        } else if (data && data.summary_calculations?.attitudinal_communication_style) {
            const acsRaw = data.summary_calculations.attitudinal_communication_style;
            const compositeProactivity = (acsRaw.proactivity_score || 0) / 2;
            const compositeReactivity = (acsRaw.reactivity_score || 0) / 2;
            plotData = [{ x: compositeProactivity, y: compositeReactivity, name: "Your Style", fill: "#673ab7", type: "current_user" }];
        } else {
            return <Box sx={{textAlign: 'center', my:2}}><Typography color="textSecondary">ACS Graph data not available.</Typography></Box>;
        }

        const axisTicks = Array.from({ length: 19 }, (_, i) => i - 9);
        const acsGraphIntroText = "The graph below plots Composite Proactivity and Composite Reactivity scores. For individual reports, it shows your style. For team reports, it shows individual members and the team average. The closer scores are to 0, the easier it may be to flex communication styles, enhancing collaboration.";
        
        const legendPayload = [];
        const typesPresent = new Set(plotData.map(p => p.type));
        if (typesPresent.has('team_average')) legendPayload.push({ value: 'Team Average', type: 'star', id: 'avg', color: '#4caf50' }); 
        if (typesPresent.has('current_user')) legendPayload.push({ value: 'Your Style', type: 'circle', id: 'curr', color: '#ff9800' });
        if (typesPresent.has('member')) legendPayload.push({ value: 'Team Members', type: 'circle', id: 'mem', color: '#2196f3' });


        return (
            <Card sx={{ mb: 2, p: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom align="center">{title}</Typography>
                    <Typography variant="body2" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{acsGraphIntroText}</Typography>
                    <ResponsiveContainer width="90%" aspect={1}> 
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" dataKey="x" name="Proactivity" domain={[-9, 9]} ticks={axisTicks} allowDataOverflow={true}><Label value="Proactivity" offset={-15} position="insideBottom" /></XAxis>
                            <YAxis type="number" dataKey="y" name="Reactivity" domain={[-9, 9]} ticks={axisTicks} allowDataOverflow={true}><Label value="Reactivity" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} /></YAxis>
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                            <Legend verticalAlign="top" height={36} payload={legendPayload.length > 0 ? legendPayload : undefined} />
                            <ReferenceArea x1={0.05} x2={9} y1={0.05} y2={9} stroke="none" fill="#e0f7fa" fillOpacity={0.3} label={{ value: "Expressive", position: "insideTopRight", fill:"#00796b" }}/>
                            <ReferenceArea x1={-9} x2={-0.05} y1={-9} y2={-0.05} stroke="none" fill="#fff3e0" fillOpacity={0.3} label={{ value: "Analytical", position: "insideBottomLeft", fill:"#ef6c00"}}/>
                            <ReferenceArea x1={0.05} x2={9} y1={-9} y2={-0.05} stroke="none" fill="#e8f5e9" fillOpacity={0.3} label={{ value: "Driver", position: "insideBottomRight", fill:"#2e7d32"}}/>
                            <ReferenceArea x1={-9} x2={-0.05} y1={0.05} y2={9} stroke="none" fill="#fce4ec" fillOpacity={0.3} label={{ value: "Amiable", position: "insideTopLeft", fill:"#d81b60"}}/>
                            <ReferenceArea x1={-0.05} x2={0.05} fill="gray" fillOpacity={0.2} yAxisId={0} />
                            <ReferenceArea y1={-0.05} y2={0.05} fill="gray" fillOpacity={0.2} xAxisId={0} />
                            <Scatter name="Data Points" data={plotData} shape={<CustomACSCircle />} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        );
    };

    const renderWorkEnvironmentPreferences = (data) => {
        if (!data || !data.summary_calculations?.work_environment_preferences_dimensions) return null;
        const wep = data.summary_calculations.work_environment_preferences_dimensions;
        const wepIntroText = "Understanding the work environments you prefer will help you be a better collaborator in team work environments. Each of these eight dimensions represents a different aspect of working with others. The numbers that your responses generated on each dimension are merely a reflection of your preferences, not your proficiency in each domain. High numbers (greater than 2.67) and low numbers (less than 1.33) are most notable. Scores in the mid-range, between 1.33 and 2.67 merely indicate a lack of strong preferences in those domains. In newer professional environments, you may want to pay especially close attention to the Feedback, Relationships, Conflict, and Chronemics dimensions.";

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>Work Environment Preferences</Typography>
                    <Typography variant="body1" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{wepIntroText}</Typography>
                    <Grid container spacing={3}> 
                        {Object.entries(wep).map(([dimension, details]) => {
                            const score = details.average_score;
                            let scoreRangeExplanation = "";
                            const dimensionInfo = wepDimensionExplanations[dimension];

                            if (score !== null && score !== undefined && dimensionInfo) {
                                if (score <= 1.33) {
                                    scoreRangeExplanation = dimensionInfo.low;
                                } else if (score >= 2.67) {
                                    scoreRangeExplanation = dimensionInfo.high;
                                }
                            }
                            const fullDimensionExplanation = dimensionInfo ? `${dimensionInfo.measure}` : "";

                            return (
                                <Grid item xs={12} sm={6} key={dimension}>
                                    <Typography variant="subtitle1" sx={{textAlign: 'left', fontWeight:'bold'}}>{dimension}</Typography>
                                    <Typography variant="body2" sx={{textAlign: 'left', mb: 0.5}}>
                                        Calculated Number: {score !== null && score !== undefined ? score.toFixed(2) : 'N/A'} (0-4 scale)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" component="div" sx={{textAlign: 'left', whiteSpace: 'pre-line', mb: 0.5}}>
                                        {fullDimensionExplanation}
                                    </Typography>
                                    {scoreRangeExplanation && (
                                        <Typography variant="caption" color="text.primary" component="div" sx={{textAlign: 'left', whiteSpace: 'pre-line', fontStyle:'italic'}}>
                                            {scoreRangeExplanation}
                                        </Typography>
                                    )}
                                </Grid>
                            );
                        })}
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const renderWEPGraph = (data, title = "Work Environment Preferences Graph", teamReportData = null) => {
        const wepDimensionNames = Object.keys(wepDimensionExplanations);
        const memberColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE', '#A52A2A', '#5F9EA0', '#D2691E', '#FF7F50'];
        const yAxisDomain = [0, 4];
        const wepXAxisTicks = wepDimensionNames.map((_, index) => index);
        const lowScoreBg = "#e3f2fd";    
        const midScoreBg = "#f3e5f5";    
        const highScoreBg = "#e0f2f1";   

        let chartData = [];
        let legendPayload = [];

        if (teamReportData && teamReportData.members_spi_data && teamReportData.team_average_wep) {
            const teamAverages = teamReportData.team_average_wep;
            const membersData = teamReportData.members_spi_data;

            wepDimensionNames.forEach((dimName, index) => {
                if (teamAverages[dimName] !== null && teamAverages[dimName] !== undefined) {
                    chartData.push({
                        x: index, y: parseFloat(teamAverages[dimName].toFixed(2)),
                        name: `Avg: ${dimName}`, type: 'team_average_wep',
                        fill: '#e91e63', dimName: dimName,
                        isCurrentUserPoint: false 
                    });
                }
                membersData.forEach((member, memberIdx) => {
                    if (member.wep_scores && member.wep_scores[dimName] !== null && member.wep_scores[dimName] !== undefined) {
                        const isCurrentUserViewing = member.user_id.toString() === userId;
                        chartData.push({
                            x: index, y: parseFloat(member.wep_scores[dimName].toFixed(2)),
                            name: `${member.name}`, type: 'member_wep',
                            fill: isCurrentUserViewing ? '#ff9800' : memberColors[memberIdx % memberColors.length],
                            dimName: dimName, memberName: member.name,
                            isCurrentUserPoint: isCurrentUserViewing
                        });
                    }
                });
            });
            legendPayload.push({ value: 'Team Average', type: 'diamond', id: 'wep_avg', color: '#e91e63' });
            if (membersData.some(m => m.user_id.toString() === userId)) { 
                legendPayload.push({ value: 'Your Score', type: 'circle', id: 'wep_curr', color: '#ff9800' });
            }
            legendPayload.push({ value: 'Team Members', type: 'circle', id: 'wep_mem', color: memberColors[0] }); 

        } else if (data && data.summary_calculations?.work_environment_preferences_dimensions) {
            const wepDimensions = data.summary_calculations.work_environment_preferences_dimensions;
            chartData = wepDimensionNames.map((dimName, index) => ({
                x: index, 
                y: wepDimensions[dimName]?.average_score !== null && wepDimensions[dimName]?.average_score !== undefined ? parseFloat(wepDimensions[dimName].average_score.toFixed(2)) : null,
                name: dimName,
                fill: "#673ab7", 
                stroke: "#000000", 
                tooltipExplanation: wepDimensions[dimName]?.average_score !== null && wepDimensions[dimName]?.average_score !== undefined && wepDimensionExplanations[dimName] ? 
                                    (wepDimensions[dimName].average_score <= 1.33 ? wepDimensionExplanations[dimName].low : 
                                    (wepDimensions[dimName].average_score >= 2.67 ? wepDimensionExplanations[dimName].high : null)) 
                                    : null,
                type: 'individual_wep',
                isCurrentUserPoint: true 
            }));
            legendPayload.push({ value: 'Your Score', type: 'circle', id: 'wep_ind', color: '#673ab7' });
        } else {
             return <Box sx={{textAlign: 'center', my:2}}><Typography color="textSecondary">WEP Graph data not available.</Typography></Box>;
        }
        
        if (chartData.filter(d => d.y !== null).length === 0) {
            return <Box sx={{textAlign: 'center', my:2}}><Typography color="textSecondary">No WEP scores available to plot.</Typography></Box>;
        }

        return (
            <Card sx={{ mb: 2, p: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom align="center">{title}</Typography>
                    <ResponsiveContainer width="100%" height={450}> 
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 100, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                type="number" dataKey="x" name="Dimension"
                                domain={[-0.5, wepDimensionNames.length - 0.5]} ticks={wepXAxisTicks}
                                tickFormatter={(tickValue) => wepDimensionNames[tickValue] || ''} 
                                interval={0} angle={-45} textAnchor="end" dy={10} 
                                style={{ fontSize: '0.75rem' }} 
                            />
                            <YAxis type="number" dataKey="y" name="Score" domain={yAxisDomain} ticks={[0, 1, 2, 3, 4]} tickFormatter={(tick) => tick.toFixed(0)} />
                            <Tooltip 
                                cursor={{ strokeDasharray: '3 3' }} 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const dataPoint = payload[0].payload; 
                                        const scoreValue = dataPoint.y !== null ? dataPoint.y.toFixed(2) : 'N/A';
                                        let pointName = dataPoint.name;
                                        if (dataPoint.type === 'member_wep') pointName = `${dataPoint.memberName}: ${dataPoint.dimName}`;
                                        else if (dataPoint.type === 'team_average_wep') pointName = `Team Avg: ${dataPoint.dimName}`;
                                        else if (dataPoint.type === 'individual_wep') pointName = `${dataPoint.name}`;


                                        let explanation = dataPoint.tooltipExplanation; 
                                        if (teamReportData && dataPoint.type === 'team_average_wep') {
                                            explanation = dataPoint.y !== null && wepDimensionExplanations[dataPoint.dimName] ?
                                                        (dataPoint.y <= 1.33 ? wepDimensionExplanations[dataPoint.dimName].low :
                                                        (dataPoint.y >= 2.67 ? wepDimensionExplanations[dataPoint.dimName].high : "This team's preference is in the mid-range for this dimension."))
                                                        : "No specific interpretation for this score range.";
                                        } else if (teamReportData && dataPoint.type === 'member_wep') {
                                             explanation = dataPoint.y !== null && wepDimensionExplanations[dataPoint.dimName] ?
                                                        (dataPoint.y <= 1.33 ? wepDimensionExplanations[dataPoint.dimName].low :
                                                        (dataPoint.y >= 2.67 ? wepDimensionExplanations[dataPoint.dimName].high : `${dataPoint.memberName}'s preference is in the mid-range.`))
                                                        : "No specific interpretation for this score range.";
                                        }

                                        return (
                                            <Paper elevation={3} sx={{ p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                                                <Typography variant="caption" display="block" sx={{fontWeight: 'bold'}}>{pointName}</Typography>
                                                <Typography variant="caption" display="block">{`Score: ${scoreValue}`}</Typography>
                                                {explanation && (
                                                    <Typography variant="caption" display="block" sx={{mt: 0.5, maxWidth: 200, whiteSpace: 'normal'}}>
                                                        {explanation}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend verticalAlign="top" height={36} payload={legendPayload} />
                            <ReferenceArea y1={0} y2={1.33} fill={lowScoreBg} fillOpacity={0.4} label={{value: "Low Range", position:"insideTopLeft", fill:"#555", fontSize:11, dy: 5}}/>
                            <ReferenceArea y1={1.33} y2={2.67} fill={midScoreBg} fillOpacity={0.4} label={{value: "Mid Range", position:"insideTopLeft", fill:"#555", fontSize:11, dy: 5}} />
                            <ReferenceArea y1={2.67} y2={4} fill={highScoreBg} fillOpacity={0.4} label={{value: "High Range", position:"insideTopLeft", fill:"#555", fontSize:11, dy: 5}}/>
                            <Scatter name="Scores" data={chartData.filter(d => d.y !== null)} shape={<CustomTeamWEPShape />} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        );
    };

    const renderAdjectives = (data) => {
        if (!data || !data.sections?.Adjectives?.items?.[0]?.selected_adjectives) return null;
        const adjectives = data.sections.Adjectives.items[0].selected_adjectives;
        const adjectivesIntroText = "You were invited to choose up to seven descriptive words and phrases that you felt best describe you. Taken together, the words you selected describe your identity or who you believe you are at your core. As you learn more about yourself, you may find that your identity either aligns or misaligns with your actual professional reputation. Here is the list of words you selected for yourself:";

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>Adjectives You Selected</Typography> 
                    <Typography variant="body1" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{adjectivesIntroText}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {adjectives.map((adj, index) => (
                            <Chip key={index} label={adj} />
                        ))}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const rephraseInformalRole = (roleText) => {
        if (!roleText) return "";
        let rephrased = roleText;
        rephrased = rephrased.replace(/\bI am\b/gi, "you are");
        rephrased = rephrased.replace(/\bI organize\b/gi, "you organize");
        rephrased = rephrased.replace(/\bI enjoy\b/gi, "you enjoy");
        rephrased = rephrased.replace(/\bI provide\b/gi, "you provide");
        rephrased = rephrased.replace(/\bI advocate\b/gi, "you advocate");
        rephrased = rephrased.replace(/\bmy views are\b/gi, "your views are");
        rephrased = rephrased.replace(/\bI recognize\b/gi, "you recognize");
        rephrased = rephrased.replace(/\bI spot\b/gi, "you spot");
        rephrased = rephrased.replace(/\bI am concerned\b/gi, "you are concerned");
        rephrased = rephrased.replace(/\bI\b/g, "you"); 
        rephrased = rephrased.replace(/\bmy\b/g, "your");
        rephrased = rephrased.replace(/\bme\b/g, "you");
        rephrased = rephrased.replace(/\bmyself\b/g, "yourself");
        rephrased = rephrased.replace(/^you\b/, "You"); 
        rephrased = rephrased.replace(/\.\s*you\b/g, ". You");
        rephrased = rephrased.replace(/\?\s*you\b/g, "? You");
        rephrased = rephrased.replace(/!\s*you\b/g, "! You");
        return rephrased;
    }

    const renderInformalTeamRole = (data) => {
        if (!data || !data.sections?.['Informal Team Roles']?.items?.[0]?.selected_role) return null;
        const originalRoleText = data.sections['Informal Team Roles'].items[0].selected_role;
        const rephrasedRoleText = rephraseInformalRole(originalRoleText);
        const informalRoleIntroText = "Regardless of formal titles in a work environment, every member of a working group or team naturally assumes certain informal roles during the group's lifecycle. The highest functioning and most successful teams have at least one member filling each of the five individual team roles, Results, Relationships, Pragmatism, Process, and Innovation. You will find it helpful to discuss your self-selected informal role below and the preferred roles of your colleagues as you join new teams or welcome new teammates onto your existing teams.";

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>Your Informal Team Role</Typography> 
                    <Typography variant="body1" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{informalRoleIntroText}</Typography>
                    <Typography variant="body1" sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>{rephrasedRoleText}</Typography>
                </CardContent>
            </Card>
        );
    };

    const renderPersonalMotivators = (data) => {
        if (!data || !data.sections?.['Personal Motivators and Values']?.items) return null;
        const motivators = data.sections['Personal Motivators and Values'].items;
        const strongInterests = data.summary_calculations?.personal_motivators_strong_interests || [];
        const pmvIntroText = "You are more likely to succeed and thrive when you work in environments that most closely align with your own personal values. Understanding your key motivators also helps you keep yourself on track toward your goals. Your values define what motivates you. Your motivators, in turn, can be grouped into larger interest areas that can help you clarify your professional trajectory and assess organizational culture fit when opportunities arise.";

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>Personal Motivators and Values</Typography> 
                    <Typography variant="body1" sx={{mb: 2, textAlign: 'left', whiteSpace: 'pre-line'}}>{pmvIntroText}</Typography>
                    {strongInterests.length > 0 && (
                        <Box sx={{mb: 2}}>
                            <Typography variant="subtitle1" color="primary" sx={{textAlign: 'left', fontWeight:'bold'}}>Your Strong Interest Areas:</Typography>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, mt: 1 }}>
                                {strongInterests.map(interest => <Chip key={interest} label={interest} color="success" />)}
                            </Box>
                             {strongInterests.map(interest => (
                                <Box key={`explanation-${interest}`} sx={{mt:0.5, mb: 1.5}}>
                                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>
                                        {pmvInterestAreaDefinitions[interest] || `Details for ${interest} will appear here.`}
                                    </Typography>
                                </Box>
                             ))}
                        </Box>
                    )}
                    <Typography variant="h6" sx={{mt:3, mb:1, textAlign: 'left'}}>Your Ratings on All Motivators:</Typography>
                    {Object.entries(motivators.reduce((acc, curr) => {
                        const area = curr.dimension_name || 'Uncategorized';
                        if (!acc[area]) acc[area] = [];
                        acc[area].push(curr);
                        return acc;
                    }, {})).map(([area, items]) => (
                        <Box key={area} sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle1" sx={{textAlign: 'left', fontWeight: 'medium'}}>{area}</Typography>
                            <List dense disablePadding> 
                                {items.map(item => (
                                    <ListItem key={item.question_id} sx={{pl:0, alignItems: 'flex-start'}}>
                                        <ListItemText 
                                            primary={<Typography variant="body2" sx={{textAlign: 'left'}}>{item.question_text}</Typography>}
                                            secondary={<Typography variant="caption" sx={{textAlign: 'left'}}>{`Your rating: ${item.score} / 100`}</Typography>} 
                                            sx={{m:0}}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                </CardContent>
            </Card>
        );
    };

    const renderInformalRolesTeamReport = (teamReport) => {
        if (!teamReport.informal_role_counts || !teamReport.members_spi_data) return null;

        const roleNames = ["Results", "Relationships", "Process", "Innovation", "Pragmatist"];
        const memberCount = teamReport.members_spi_data.length;
        
        // Group members by their selected role
        const membersByRole = roleNames.reduce((acc, role) => {
            acc[role] = teamReport.members_spi_data
                .filter(member => member.informal_role === role)
                .map(member => member.name);
            return acc;
        }, {});

        // Determine which statement to show for each role
        const roleFeedback = roleNames.map(role => {
            const count = teamReport.informal_role_counts[role] || 0;
            const percentage = memberCount > 0 ? (count / memberCount) * 100 : 0;
            
            if (percentage >= 50) {
                return informalRoleFeedbackStatements[role].high;
            } else if (percentage > 0) {
                return informalRoleFeedbackStatements[role].medium;
            } else {
                return informalRoleFeedbackStatements[role].none;
            }
        });

        return (
            <Card sx={{ mb: 2, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{textAlign: 'left'}}>Team Informal Roles Distribution</Typography>
                    <TableContainer component={Paper} elevation={1} sx={{mb: 2}}>
                        <Table size="small" aria-label="informal roles team distribution">
                            <TableHead>
                                <TableRow>
                                    {roleNames.map(role => <TableCell key={role} align="center" sx={{fontWeight: 'bold'}}>{role}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {roleNames.map(role => (
                                        <TableCell key={role} align="center" sx={{verticalAlign: 'top'}}>
                                            {membersByRole[role].length > 0 ? (
                                                <List dense disablePadding>
                                                    {membersByRole[role].map((name, idx) => (
                                                        <ListItem key={idx} disablePadding><ListItemText primary={name} primaryTypographyProps={{variant:'body2'}} /></ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic'}}>No team members selected this informal team role.</Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom sx={{textAlign: 'left', mt: 3}}>Team Informal Roles Analysis</Typography>
                    {roleFeedback.map((feedback, index) => (
                        <Typography key={index} paragraph sx={{textAlign: 'left'}}>{feedback}</Typography>
                    ))}
                </CardContent>
            </Card>
        );
    };

    const renderTeamSPIReport = () => {
        if (loadingTeam) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
        if (teamError) return <Alert severity="error" sx={{ m: 2 }}>{teamError}</Alert>;
        
        // Updated logic to check for the placeholder message from the backend
        if (teamSpiReport && teamSpiReport.message && teamSpiReport.members_with_data_count < 3) {
             return (
                <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                    Team SPI insights will be available once at least three members of your team have completed the Self-Perception Inventory. 
                    (Currently {teamSpiReport.members_with_data_count} completed)
                </Alert>
            );
        }
        if (!teamSpiReport || !teamSpiReport.members_spi_data) { 
            return <Alert severity="info" sx={{ m: 2, textAlign: 'left' }}>Team SPI report data is not available for your current team and cohort.</Alert>;
        }

        const { 
            team_name, 
            cohort_name, 
            key_team_motivator_drivers,
            ai_generated_summary_and_advice 
        } = teamSpiReport;

        const teamAcsChartData = teamSpiReport.members_spi_data.map(member => ({
            x: member.acs_proactivity,
            y: member.acs_reactivity,
            name: member.name,
            fill: userId === member.user_id.toString() ? '#ff9800' : '#2196f3', 
            type: userId === member.user_id.toString() ? 'current_user' : 'member'
        }));
        if (teamSpiReport.team_average_acs && teamSpiReport.team_average_acs.proactivity !== null) {
            teamAcsChartData.push({
                x: teamSpiReport.team_average_acs.proactivity,
                y: teamSpiReport.team_average_acs.reactivity,
                name: `Team Average (${teamSpiReport.team_average_acs.style_name})`,
                fill: '#4caf50',
                type: 'team_average'
            });
        }
        
        return (
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: 'grey.50' }}>
                <Typography variant="h5" component="h2" gutterBottom align="center" sx={{color: 'primary.main'}}>
                    Team SPI Insights: {team_name} (Cohort: {cohort_name})
                </Typography>
                <Divider sx={{ my: 2 }} />

                {renderACSGraph(null, "Team Attitudinal Communication Styles", teamAcsChartData)}
                
                {renderWEPGraph(null, "Team Work Environment Preferences", teamSpiReport, true)}

                <Card sx={{ mb: 2, mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{textAlign: 'left'}}>Team Culture & Motivators</Typography>
                        {key_team_motivator_drivers && key_team_motivator_drivers.length > 0 ? (
                            key_team_motivator_drivers.map((driver, index) => (
                                <Box key={index} sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                                        {driver.motivator}: <Chip label={driver.tendency.toUpperCase()} color={driver.tendency === 'high' ? 'success' : 'warning'} size="small" /> 
                                        <Typography variant="caption" sx={{ml:1}}>({driver.percentage}% of team)</Typography>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>
                                        {teamCultureStatements[driver.motivator]?.[driver.tendency] || "General interpretation for this motivator tendency."}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography color="text.secondary" sx={{textAlign: 'left'}}>No strong team-wide motivator drivers identified based on the 50% threshold.</Typography>
                        )}
                    </CardContent>
                </Card>
                
                {renderInformalRolesTeamReport(teamSpiReport)}

                {ai_generated_summary_and_advice && (
                    <Card sx={{ mb: 2, mt: 3 }}>
                        <CardContent>
                             <Typography variant="h6" gutterBottom sx={{textAlign: 'left'}}>AI-Generated Team Summary & Advice</Typography>
                             <Box sx={{whiteSpace: 'pre-line', backgroundColor: 'background.paper', p:2, borderRadius:1, border: '1px solid', borderColor: 'divider'}}>
                                {ai_generated_summary_and_advice.split('\n').map((line, index) => {
                                    const isHeading = /^(Team ACS Profile|Work Environment Preferences Analysis|Team Culture & Motivators Analysis|Recommendations for Collaboration)/i.test(line.trim());
                                    return (
                                        <Typography 
                                            key={index} 
                                            variant={isHeading ? "subtitle1" : "body2"} 
                                            sx={{
                                                fontWeight: isHeading ? 'bold' : 'normal', 
                                                mt: isHeading && index > 0 ? 1.5 : 0, 
                                                mb: 0.5,
                                                textAlign: 'left' 
                                            }}
                                        >
                                            {line}
                                        </Typography>
                                    );
                                })}
                             </Box>
                        </CardContent>
                    </Card>
                )}
            </Paper>
        );
    };

    const shouldRenderTeamReportAccordion = teamSpiReport && 
                                 teamSpiReport.members_spi_data && 
                                 teamSpiReport.members_spi_data.length >= 3 &&
                                 teamSpiReport.team_name && teamSpiReport.team_name.toLowerCase() !== 'none';

    const displayTeamReportPlaceholderMessage = teamSpiReport && 
                                            teamSpiReport.message && 
                                            teamSpiReport.team_name && teamSpiReport.team_name.toLowerCase() !== 'none';

    const displayTeamNoneMessage = teamSpiReport && 
                                 teamSpiReport.team_name && 
                                 teamSpiReport.team_name.toLowerCase() === 'none';

    const displayNoDataForTeamMessage = teamSpiReport &&
                                      teamSpiReport.message === 'No SPI data found for any team members in this cohort.' &&
                                      teamSpiReport.team_name && teamSpiReport.team_name.toLowerCase() !== 'none';


    if (loadingIndividual) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    if (!individualResults || !individualResults.user_info) { 
        return <Alert severity="info" sx={{ m: 2 }}>Your SPI results data is not available or user information is missing.</Alert>;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Self-Perception Inventory Results
                </Typography>
                <Typography variant="subtitle1" align="center" gutterBottom>
                    For: {individualResults.user_info.name || `User ID: ${individualResults.user_id}`} 
                    {cohortId && ` (Cohort ID: ${cohortId})`} 
                </Typography>
                <Divider sx={{ my: 2 }} />

                {renderAttitudinalCommunicationStyles(individualResults)}
                {renderACSGraph(individualResults)} 
                {renderWorkEnvironmentPreferences(individualResults)}
                {renderWEPGraph(individualResults, "Your Work Environment Preferences")} 
                
                {renderAdjectives(individualResults)}
                {renderInformalTeamRole(individualResults)}
                {renderPersonalMotivators(individualResults)}

                {teamId && cohortId && ( 
                    loadingTeam ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, mt: 3 }}><CircularProgress /></Box>
                    ) : teamError ? (
                        <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>Could not load team report: {teamError}</Alert>
                    ) : shouldRenderTeamReportAccordion ? (
                        <Accordion sx={{mt: 3}} defaultExpanded> 
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="team-spi-report-content"
                                id="team-spi-report-header"
                            >
                                <Typography variant="h6">View Your Team's SPI Insights</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderTeamSPIReport()}
                            </AccordionDetails>
                        </Accordion>
                    ) : displayTeamNoneMessage ? (
                        <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                            Team SPI insights are not shown for the default 'None' team. They will be available once you are assigned to a specific work team.
                        </Alert>
                    ) : displayTeamReportPlaceholderMessage ? (
                         <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                            {teamSpiReport.message}
                        </Alert>
                    ) : displayNoDataForTeamMessage ? (
                        <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                            No SPI data found for any members of your current team. Team insights cannot be generated until at least three members, including you, have completed the SPI.
                        </Alert>
                    )
                     : null 
                )}
            </Paper>
        </Container>
    );
}

export default SelfPerceptionInventoryResults;
